import { useRef, useState } from "react";
import { Image as ImageIcon, Download, Share2 } from "lucide-react";
import { toast } from "sonner";

const W = 1200;
const H = 630;

function drawCard(canvas, stats, user) {
  const ctx = canvas.getContext("2d");
  canvas.width = W;
  canvas.height = H;

  // Background
  ctx.fillStyle = "#09090b";
  ctx.fillRect(0, 0, W, H);

  // Subtle grid
  ctx.strokeStyle = "rgba(255,255,255,0.03)";
  for (let x = 0; x < W; x += 40) {
    ctx.beginPath();
    ctx.moveTo(x, 0);
    ctx.lineTo(x, H);
    ctx.stroke();
  }
  for (let y = 0; y < H; y += 40) {
    ctx.beginPath();
    ctx.moveTo(0, y);
    ctx.lineTo(W, y);
    ctx.stroke();
  }

  // Brand
  ctx.fillStyle = "#00D4FF";
  ctx.font = "600 14px 'JetBrains Mono', monospace";
  ctx.fillText("MARTINGALE · PLAYBOOK", 60, 70);

  // Headline
  ctx.fillStyle = "#ffffff";
  ctx.font = "500 64px 'Inter Tight', system-ui, sans-serif";
  const name = user?.name || (user?.email || "").split("@")[0] || "Tester";
  ctx.fillText(`${name}'s bankroll.`, 60, 170);

  // Date
  ctx.fillStyle = "#71717a";
  ctx.font = "12px 'JetBrains Mono', monospace";
  const today = new Date().toLocaleDateString(undefined, {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
  ctx.fillText(today.toUpperCase(), 60, 205);

  // Stat blocks
  const net = stats.net_profit || 0;
  const blocks = [
    { label: "BANKROLL", value: `$${(stats.current_bankroll || 0).toLocaleString()}`, color: "#ffffff" },
    {
      label: "NET P/L",
      value: `${net >= 0 ? "+" : ""}$${net.toLocaleString()}`,
      color: net >= 0 ? "#00D4FF" : "#ff4d4d",
    },
    {
      label: "RECORD",
      value: `${stats.wins || 0}–${stats.losses || 0}`,
      color: "#ffffff",
      sub: `${stats.win_rate_pct || 0}% WIN RATE`,
    },
  ];
  blocks.forEach((b, i) => {
    const x = 60 + i * 370;
    const y = 280;
    // Block border
    ctx.strokeStyle = "#27272a";
    ctx.lineWidth = 1;
    ctx.strokeRect(x, y, 340, 210);
    // Label
    ctx.fillStyle = "#a1a1aa";
    ctx.font = "600 12px 'JetBrains Mono', monospace";
    ctx.fillText(b.label, x + 20, y + 38);
    // Value
    ctx.fillStyle = b.color;
    ctx.font = "500 64px 'Inter Tight', system-ui, sans-serif";
    ctx.fillText(b.value, x + 20, y + 125);
    // Sub
    if (b.sub) {
      ctx.fillStyle = "#71717a";
      ctx.font = "11px 'JetBrains Mono', monospace";
      ctx.fillText(b.sub, x + 20, y + 170);
    }
  });

  // Footer
  ctx.fillStyle = "#00D4FF";
  ctx.font = "600 14px 'JetBrains Mono', monospace";
  ctx.fillText("MARTINGALEPLAYBOOK — AI PICKS · MARTINGALE MATH · ARBITRAGE", 60, 570);
  ctx.fillStyle = "#52525b";
  ctx.font = "10px 'JetBrains Mono', monospace";
  ctx.fillText("GAMBLE RESPONSIBLY · 1-800-GAMBLER", 60, 595);
}

export default function ShareBankrollCard({ stats, user }) {
  const canvasRef = useRef(null);
  const [generated, setGenerated] = useState(false);

  const generate = () => {
    if (!canvasRef.current || !stats) return;
    drawCard(canvasRef.current, stats, user);
    setGenerated(true);
  };

  const download = () => {
    if (!canvasRef.current || !generated) generate();
    const url = canvasRef.current.toDataURL("image/png");
    const a = document.createElement("a");
    a.href = url;
    a.download = `martingale-bankroll-${Date.now()}.png`;
    a.click();
  };

  const share = async () => {
    if (!canvasRef.current) return;
    if (!generated) {
      drawCard(canvasRef.current, stats, user);
      setGenerated(true);
    }
    canvasRef.current.toBlob(async (blob) => {
      if (!blob) return;
      const file = new File([blob], "bankroll.png", { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({
            files: [file],
            title: "My bankroll",
            text: "Tracking my Martingale bankroll with @MartingalePlaybook",
          });
        } catch (err) {
          // Only silently ignore the expected AbortError from user cancelling
          // the native share sheet. Real failures still surface to the user.
          if (err?.name !== "AbortError") {
            toast.message("Share failed — try downloading instead");
          }
        }
        return;
      }
      // Fallback — download
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      toast.message("Image opened — save it and share manually");
    }, "image/png");
  };

  return (
    <div className="border border-zinc-800 p-6" data-testid="share-card">
      <div className="overline neon-text mb-3 flex items-center gap-2">
        <ImageIcon size={12} strokeWidth={1.5} /> SHAREABLE BANKROLL CARD
      </div>
      <p className="text-sm text-zinc-400 mb-4 leading-relaxed">
        Generate a branded PNG of your weekly P/L — perfect for Twitter / Reddit /
        Discord flex.
      </p>
      <canvas
        ref={canvasRef}
        width={W}
        height={H}
        data-testid="share-canvas"
        className="w-full border border-zinc-800 bg-zinc-950"
        style={{ aspectRatio: `${W} / ${H}` }}
      />
      <div className="mt-4 flex gap-2">
        <button
          onClick={generate}
          data-testid="share-generate"
          className="flex-1 py-2 border border-zinc-700 text-zinc-300 hover:border-[#00D4FF] hover:text-[#00D4FF] font-mono text-xs uppercase tracking-widest"
        >
          Generate
        </button>
        <button
          onClick={download}
          data-testid="share-download"
          disabled={!stats}
          className="flex items-center justify-center gap-2 px-4 py-2 border border-zinc-700 text-zinc-300 hover:border-[#00D4FF] hover:text-[#00D4FF] font-mono text-xs uppercase tracking-widest disabled:opacity-50"
        >
          <Download size={12} strokeWidth={1.5} /> Download
        </button>
        <button
          onClick={share}
          data-testid="share-share"
          disabled={!stats}
          className="flex items-center justify-center gap-2 px-4 py-2 bg-[#00D4FF] text-black font-mono text-xs uppercase tracking-widest hover:brightness-110 disabled:opacity-50"
        >
          <Share2 size={12} strokeWidth={1.5} /> Share
        </button>
      </div>
    </div>
  );
}
